import React from 'react'
import { Route, Redirect, IndexRedirect } from 'react-router'
import { getApplication } from 'app/Application'

import { onLoggedInRouteEnter, onProtectedRouteEnter, onLoggedInRouteChange } from './RouteConfig'
import { actions as UserActions } from 'app/modules/user'
import * as ProfileActions from 'app/modules/profile/actions'
import * as SocketActions from 'app/modules/socket/actions'
import { fetchEmbed } from 'app/modules/embed/actions'
import { SocketCluster } from 'app/modules/socket/constants'

import ConversationRoutes from 'app/routes/ConversationRoutes'
import EndUserRoutes from 'app/routes/EndUserRoutes'
import SettingsRoutes from 'app/routes/SettingsRoutes'
import { Routes as CustomizeRoutes } from 'app/routes/CustomizeRoutes'
import { Path as CustomizePath } from 'app/routes/CustomizeRoutes'
import DriftAdminPanelRoutes from 'app/routes/DriftAdminPanelRoutes'

import ApplicationComponent from 'components/ApplicationComponent'
import NotFoundPage from 'components/NotFoundPage'
import SlackLander from 'components/SlackLander'
import SlackInstall from 'components/SlackInstall'
import HealthCheck from 'components/HealthCheck'
import Logout from 'components/Logout'
import { container as MobileLander } from 'components/MobileLander'
import OAuth from 'components/Integration/OAuth'
import WhoAmI from 'components/WhoAmI'
import Presence from 'components/Presence'
import IntegrationLander from 'components/Integration/Lander'
import CustomizeTemplate from 'components/Customize/CustomizeTemplate'
import ConversationStats from 'components/ConversationStats/RootDashboardRedirect'
import Unsubscribe from 'components/customer/Unsubscribe'
import { Routes as PlaybookRoutes } from 'components/playbook/routes'
import { APP_URL } from 'constants/ResourcesConstants'
import { fetchIntegrationStates } from 'app/modules/integrations/actions'
import { enterRootAppRoute } from 'app/modules/driftAppMetrics/actions'

const DASHBOARD_PATH = '/dashboard'
const CONVERSATION_STATS_PATH = '/conversation-stats'

const SalesforceRedirectPage = () => {
  window.location.href = `${APP_URL}/settings2/integrations/detail/salesforce`
  return null
}

const On24RedirectPage = () => {
  window.location.href = `${APP_URL}/settings2/integrations/detail/on24`
  return null
}

const OutreachRedirectPage = () => {
  window.location.href = `${APP_URL}/settings2/integrations/detail/outreach`
  return null
}

const ZapierRedirectPage = () => {
  window.location.href = `${APP_URL}/settings2/integrations/detail/zapier`
  return null
}

const PlaceholderDashboard = () => {
  //this is a hack to fix broken signups.
  //the style thing is to avoid a flash of the nav before we redirect.
  document.body.style.display = 'none'
  window.location.href = `${APP_URL}/home${window.location.search}`
  return null
}

const createRoutes = store => (
  <Route path="/" onEnter={() => store.dispatch(enterRootAppRoute())}>
    <Route path="accept-integration/:integration" component={IntegrationLander} />
    <Route path="/logout" component={Logout} />
    {/* Redirect customer-web salesforce page to settings-web */}
    <Route path="/settings/setup/salesforce" component={SalesforceRedirectPage} />
    {/* Redirect customer-web on24 page to settings-web */} 
    <Route path="/settings/setup/on24" component={On24RedirectPage} />
    {/* Redirect customer-web outreach page to settings-web */}
    <Route path="/settings/setup/outreach" component={OutreachRedirectPage} />
    {/* Redirect customer-web zapier page to settings-web */}
    <Route path="/settings/setup/zapier" component={ZapierRedirectPage} />
    {/* Redirect customer-web unsubscribe page to settings-web */}
    <Route path="/settings/integrations/salesforce" component={SalesforceRedirectPage} />
    <Route path="/settings/salesforce/config" component={SalesforceRedirectPage} />
    <Route path="/settings/salesforce/mapping/attributes" component={SalesforceRedirectPage} />
    <Route path="/settings/salesforce/mapping/attributes/metrics" component={SalesforceRedirectPage} />
    <Route component={ApplicationComponent}>
      <Route path="healthcheck" component={HealthCheck} />
      <Redirect from="/import-end-users" to="/users/import" />
      <Redirect from="/integrate" to="/settings2/integrations" />
      <Redirect from="/setup/segment" to="/settings/setup/install" />
      <Redirect from="/integrations" to="/settings2/integrations" />
      <Redirect from="/assistant" to="/settings2/assistant" />
      <Redirect from="/profile" to="/settings2/profile" />
      <Redirect from="/setup" to="/settings2/integrations" />
      <Redirect from="/setup/:integration" to="/settings/setup/:integration" />
      <Redirect from="/settings/configure" to="/customize" />
      <Redirect from="/integrate/:integration" to="/settings/setup/:integration" />
      <Redirect from="/upgrade" to="/settings/billing" />
      <Redirect from="/settings2/integrations/segment" to="/settings/widget" />
      <Route path="setup-slack" component={SlackInstall} />
      <Route
        onEnter={async (nextState, replace, callback) => {
          const processingCredential = await onLoggedInRouteEnter(getApplication(), nextState, replace, callback)
          callback()

          if (processingCredential) {
            return
          }

          await store.dispatch(UserActions.fetchCurrentUser()).then(function() {
            store.dispatch(UserActions.fetchOrgUsers())
            store.dispatch(fetchEmbed())
            store.dispatch(SocketActions.authenticate({ clusterName: SocketCluster.PUSH }))
            store.dispatch(fetchIntegrationStates())
            return store.dispatch(SocketActions.authenticate({ clusterName: SocketCluster.MESSAGES }))
          })
          return store.dispatch(ProfileActions.fetchCurrentProfile())
        }}
        onChange={(prevState, nextState, replace, callback) => {
          onLoggedInRouteChange(prevState, nextState, replace, callback)
        }}
      >
        <Route path="mobile-lander" component={MobileLander} />
        <IndexRedirect to={DASHBOARD_PATH} />
        {PlaybookRoutes(store)}
        <Route path="oauth" component={OAuth} />
        <Route path="/debug/whoami" component={WhoAmI} />
        {DriftAdminPanelRoutes}
        <Route path="/start-slack(/:slackUserId)" component={SlackLander} />
        <Route
          onChange={function(prevState, nextState, replace, callback) {
            return onProtectedRouteEnter(getApplication(), nextState, replace, callback)
          }}
          onEnter={function(nextState, replace, callback) {
            return onProtectedRouteEnter(getApplication(), nextState, replace, callback)
          }}
        >
          <Route path={DASHBOARD_PATH} component={PlaceholderDashboard}/>
          {ConversationRoutes(store)}
          <Route path={CONVERSATION_STATS_PATH} component={ConversationStats} />
          {SettingsRoutes(store)}
          <Route path={CustomizePath} component={CustomizeTemplate}>
            {CustomizeRoutes}
          </Route>
          {EndUserRoutes(store)}
          <Route path="/live" component={Presence} />
        </Route>
        <Route path="*" component={NotFoundPage} />
      </Route>
    </Route>
  </Route>
)

export default createRoutes



// WEBPACK FOOTER //
// ./src/scripts/app/routes.js